import React, { useState } from "react";
import { z } from "zod";
import { Timestamp } from "firebase/firestore";
import DateGeneric from "../../../custom-ui/old-ui/DateGeneric";
import ToastAlert from "../../../custom-ui/toast-alert/ToastAlert";
import { getJobsByMakeLiveDateRange } from "../../../../data/jobDetailsNewCollection";
import { csvDownload } from "../../AdminMetricDashboard/ExportCsv";
import DownloadIconWithLabel from "../../AdminMetricDashboard/DownLoadIcon";
import toast from "react-hot-toast";

// Zod schema for validating date range
const dateRangeSchema = z
    .object({
        startDate: z.date({ required_error: "Start date is required" }),
        endDate: z.date({ required_error: "End date is required" }),
    })
    .refine(
        (data) => data.endDate >= data.startDate,
        {
            path: ["endDate"],
            message: "End date cannot be before start date",
        }
    );

const JobStats = () => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleDownload = async () => {
        setError("");
        setLoading(true);

        const result = dateRangeSchema.safeParse({ startDate, endDate });
        if (!result.success) {
            const issue = result.error.errors[0];
            setError(
                issue.message === "Expected date, received null"
                    ? "Please select both start and end dates."
                    : issue.message
            );
            setLoading(false);
            return;
        }

        const startTimestamp = Timestamp.fromDate(result.data.startDate);
        const endTimestamp = Timestamp.fromDate(result.data.endDate);

        try {
            const jobs = await getJobsByMakeLiveDateRange(startTimestamp, endTimestamp);

            const filteredData = jobs.filter((job: any) => {
                if (!job?.makeLiveDate) return false;
                const jobTimestamp = job.makeLiveDate.seconds;
                return (
                    jobTimestamp >= startTimestamp.seconds &&
                    jobTimestamp <= endTimestamp.seconds
                );
            });

            const finalDataToDownload = filteredData.map((job: any) => ({
                "Job Title": job?.jobName || "",
                "Employer Name": job?.companyFullName || "",
                "View Count": job?.clicksCounter || 0,
                "Apply Now Count": job?.applyNowCounter || 0,
            }));

            if (!finalDataToDownload.length) {
                throw new Error("No data found in the selected date range.");
            }

            csvDownload(finalDataToDownload, "JobStats_By_Date_Range");
            toast.success("CSV downloaded successfully.");
        } catch (err: any) {
            console.error("Error downloading job stats:", err);
            setError(err?.message || "Something went wrong while downloading data.");
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="rounded-lg mt-3 bg-white border p-6 bg-card text-card-foreground shadow-sm">
            {/* Header */}
            <div className=" mb-6">
                <p className="text-2xl font-extrabold text-[#1E596D] ">Jobs Stats</p>
                <p className="text-lg text-instant-teams-teal-Main">Generate report for job statistics within a date range</p>
            </div>

            {/* Date Pickers */}
            <div className="gap-2 grid grid-cols-2">
                <div className="w-full">
                    <DateGeneric
                        name="startDate"
                        display="From Date"
                        value={startDate}
                        onChange={setStartDate}
                        timestamp={true}
                        required
                        dateLimit="past"
                    />
                </div>
                <div className="w-full">
                    <DateGeneric
                        name="endDate"
                        display="To Date"
                        value={endDate}
                        onChange={setEndDate}
                        timestamp={true}
                        required
                        dateLimit="past"
                    />
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mt-1">
                    <p className="text-red-500  text-lg">{error}</p>
                </div>
            )}

            {/* Download Button */}
            <div
                onClick={handleDownload}
                className="flex cursor-pointer bg-instant-teams-teal-Main py-3 mt-5 rounded-md text-white justify-center items-center"
            >
                {loading ? (
                    <p className="text-lg text-white">Downloading...</p>
                ) : (
                    <>
                       
                        <div className="flex items-start mr-3">
                            <DownloadIconWithLabel
                                tooltipText="Download job stats within the selected date range."
                                isFromEmpMetric={true}
                                viewDown={true}
                            />
                            </div>
                             <p className="text-lg">Download Job Stats Report</p>
                    </>
                )}
            </div>

            <ToastAlert />
        </div>
    );
};

export default JobStats;
